/**
 * Compact floating status notifications. Temporary operation feedback
 * (loading, success, warning, error) overlays the page instead of pushing layout.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './status-toast.css';
import { EnsMark } from './EnsLoader';

export type StatusToastTone = 'success' | 'loading' | 'warning' | 'error' | 'info';

export interface StatusToastItem {
  id: string;
  tone: StatusToastTone;
  title: string;
  detail?: string;
  /** Hide automatically. Omitted or 0 keeps the toast until it is removed. */
  duration?: number;
  dismissible?: boolean;
  onDismiss?: () => void;
  action?: { label: string; onClick: () => void };
}

const Icon: React.FC<{ tone: StatusToastTone }> = ({ tone }) => {
  if (tone === 'loading') return <EnsMark size="sm" icon={16} />;
  if (tone === 'error') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M12 8v5M12 16.5h.01" />
      </svg>
    );
  }
  if (tone === 'warning') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 8v5M12 16.5h.01" />
        <path d="M10.3 4.8 2.8 18a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 4.8a2 2 0 0 0-3.4 0Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
};

const ToastCard: React.FC<{ item: StatusToastItem }> = ({ item }) => {
  useEffect(() => {
    if (!item.duration || !item.onDismiss) return;
    const t = window.setTimeout(item.onDismiss, item.duration);
    return () => window.clearTimeout(t);
  }, [item.duration, item.onDismiss, item.id]);

  return (
    <div className={`stx stx-${item.tone}`} role={item.tone === 'error' || item.tone === 'warning' ? 'alert' : 'status'}>
      <span className="stx-ico" aria-hidden="true">
        <Icon tone={item.tone} />
      </span>
      <div className="stx-copy">
        <strong>{item.title}</strong>
        {item.detail && <span>{item.detail}</span>}
        {item.action && (
          <button type="button" className="stx-act" onClick={item.action.onClick}>
            {item.action.label}
          </button>
        )}
      </div>
      {item.dismissible && item.onDismiss && (
        <button type="button" className="stx-x" aria-label="Dismiss" onClick={item.onDismiss}>
          ×
        </button>
      )}
      {item.duration ? <i className="stx-bar" style={{ animationDuration: `${item.duration}ms` }} /> : null}
    </div>
  );
};

export const StatusToasts: React.FC<{ items: StatusToastItem[] }> = ({ items }) => {
  if (!items.length || typeof document === 'undefined') return null;
  return createPortal(
    <div className="stx-stack" aria-live="polite">
      {items.map((item) => (
        <ToastCard key={item.id} item={item} />
      ))}
    </div>,
    document.body,
  );
};
